import { useFeatureFlags } from './useFeatureFlags'
import * as consts from './featureFlags.constants'
import { FeatureFlagTypeMap } from '../App/app.api'
import { FeatureFlagsContextType } from './FeatureFlags.context'
import { FEATURE_FLAG_DEFAULTS, PAGE_FEATURE_FLAGS_MAP } from './featureFlags.constants'
import { getPathPrefixRegex } from '../Permissions/pagePermissionUtils'
import { useMemo } from 'react'

export function determineFeatureFlagValueFunction(featureFlags: FeatureFlagsContextType, flag: keyof FeatureFlagTypeMap) {
  const defaultValue = FEATURE_FLAG_DEFAULTS.get(flag) === undefined ? null : FEATURE_FLAG_DEFAULTS.get(flag)
  switch (featureFlags.launchDarklyProps?.implementation) {
    case consts.PROPERTY_FILE_IMPLEMENTATION: {
      const fileProps = featureFlags.launchDarklyProps.properties
      const flagValue = fileProps[flag]
      if (flagValue === undefined || flagValue === null) {
        return defaultValue
      }
      return fileProps[flag]
    }
    case consts.LAUNCHDARKLY_IMPLEMENTATION: {
      const flagValue = featureFlags.featureFlagClient?.variation(flag)
      if (flagValue === undefined || flagValue === null) {
        return defaultValue
      }
      return flagValue
    }
    default:
      return defaultValue
  }
}

export function useFeatureFlagValue<FlagName extends keyof FeatureFlagTypeMap>(flag: FlagName): string | number | boolean | undefined {
  const featureFlags = useFeatureFlags()

  return determineFeatureFlagValueFunction(featureFlags, flag)
}


export const getRegexPathFlags = (path: string):  keyof FeatureFlagTypeMap | null => {
  PAGE_FEATURE_FLAGS_MAP.forEach((flag, page) => {
    if (getPathPrefixRegex(page).test(path)) {
      return flag
    }
  })
  return null
}

/**
 * Returns a function which can be used to determine if a path is available based on feature flags
 */
export function useIsPathAvailable(): (path: string) => boolean {
  const featureFlags = useFeatureFlags()

  const trueFlags = useMemo(() => {
    const flags = new Set<keyof FeatureFlagTypeMap>()
    consts.PAGE_FEATURE_FLAGS_MAP.forEach(flag => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- this can only be correct
      const flagValue = determineFeatureFlagValueFunction(featureFlags, flag as keyof FeatureFlagTypeMap)
      if ((typeof flagValue === 'string') && flagValue.toLowerCase() === 'true' || flagValue === true) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions  -- this can only be correct
        flags.add(flag as keyof FeatureFlagTypeMap)
      }
    })
    return flags
  }, [featureFlags])

  return path => {
    let pathFlag = consts.PAGE_FEATURE_FLAGS_MAP.get(path) || null
    if (!pathFlag) {
      pathFlag = getRegexPathFlags(path)
    }
    if (!pathFlag) {
      return true
    }
    return trueFlags.has(pathFlag)
  }
}
